import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { EventAdminDTO } from "@wedding/shared-types";
import { Layout } from "../components/common/Layout.js";
import { Spinner } from "../components/common/Spinner.js";
import { listAdminEvents, createAdminEvent, adminLogout } from "../api/admin.js";
import { ApiClientError } from "../api/client.js";

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventAdminDTO[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");

  async function load() {
    try {
      const { items } = await listAdminEvents();
      setEvents(items);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate("/admin/login");
        return;
      }
      throw err;
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const event = await createAdminEvent({ name, password });
      setName("");
      setPassword("");
      await load();
      navigate(`/admin/events/${event.id}`);
    } catch (err) {
      setCreateError(err instanceof ApiClientError ? err.message : "Une erreur est survenue");
    } finally {
      setCreating(false);
    }
  }

  async function handleLogout() {
    await adminLogout();
    navigate("/admin/login");
  }

  return (
    <Layout
      title="Administration"
      actions={
        <button type="button" className="btn btn-secondary" onClick={handleLogout}>
          Déconnexion
        </button>
      }
    >
      <div className="stack">
        <form className="card stack" onSubmit={handleCreate}>
          <h2>Créer un événement</h2>
          <div className="field">
            <label htmlFor="name">Nom</label>
            <input
              id="name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mariage d'Alice & Bob"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="new-password">Mot de passe</label>
            <input
              id="new-password"
              className="input"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe donné aux invités"
              required
              minLength={4}
            />
          </div>
          {createError && <p className="error-text">{createError}</p>}
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? "Création…" : "Créer"}
          </button>
        </form>

        <h2>Événements</h2>
        {events === null ? (
          <Spinner />
        ) : events.length === 0 ? (
          <p>Aucun événement pour le moment.</p>
        ) : (
          <div className="stack">
            {events.map((event) => (
              <Link
                key={event.id}
                to={`/admin/events/${event.id}`}
                className="card"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none", color: "inherit" }}
              >
                <div>
                  <h3 style={{ margin: 0 }}>{event.name}</h3>
                  <p style={{ margin: "4px 0 0" }}>
                    {event.photoCount} photo(s) · {event.videoCount} vidéo(s) · {formatSize(event.totalSizeBytes)}
                  </p>
                </div>
                <div className="stack" style={{ alignItems: "flex-end", gap: 4 }}>
                  {event.isActive ? (
                    <span className="badge badge-success">Actif</span>
                  ) : (
                    <span className="badge badge-danger">Inactif</span>
                  )}
                  {event.expiresAt && <span className="badge">Expire le {new Date(event.expiresAt).toLocaleDateString("fr-FR")}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
